import type { MaybePromise } from '@gitbook/util-ts';

import { mergeUint8Arrays } from '../utils';
import * as protocol from './protocol';

export interface DataSyncDecoderHandlers {
    promptInit: (message: protocol.IPromptInitMessage) => MaybePromise<void>;
    init: (message: protocol.IInitMessage) => MaybePromise<void>;
    update: (message: protocol.IUpdateMessage) => MaybePromise<void>;
    updatePresence: (message: protocol.IUpdatePresenceMessage) => MaybePromise<void>;
    broadcastOperation: (message: protocol.IBroadcastOperationMessage) => MaybePromise<void>;
    broadcastPresences: (message: protocol.IBroadcastPresencesMessage) => MaybePromise<void>;
    ack: (message: protocol.IAckMessage) => MaybePromise<void>;
    rpcRequest: (message: protocol.IRPCRequestMessage) => MaybePromise<void>;
    rpcReply: (message: protocol.IRPCReplyMessage) => MaybePromise<void>;
    broadcastMarkers: (message: protocol.IbroadcastMarkersMessage) => MaybePromise<void>;
}

interface DataSyncDecoderSynchronousHandlers {
    promptInit: (message: protocol.IPromptInitMessage) => void;
    init: (message: protocol.IInitMessage) => void;
    update: (message: protocol.IUpdateMessage) => void;
    updatePresence: (message: protocol.IUpdatePresenceMessage) => void;
    broadcastOperation: (message: protocol.IBroadcastOperationMessage) => void;
    broadcastPresences: (message: protocol.IBroadcastPresencesMessage) => void;
    ack: (message: protocol.IAckMessage) => void;
    rpcRequest: (message: protocol.IRPCRequestMessage) => void;
    rpcReply: (message: protocol.IRPCReplyMessage) => void;
    broadcastMarkers: (message: protocol.IbroadcastMarkersMessage) => void;
}

export type DataSyncDecoderFn = (rawMessage: Uint8Array) => MaybePromise<void>;
export type DataSyncDecoderSynchronousFn = (rawMessage: Uint8Array) => void;

function getChunkPayload(
    chunk: protocol.IPromptInitMessage | protocol.IInitMessage | protocol.IUpdateMessage
): Uint8Array {
    if ('stateVector' in chunk && chunk.stateVector !== undefined && chunk.stateVector !== null) {
        return chunk.stateVector;
    }

    if ('update' in chunk && chunk.update !== undefined && chunk.update !== null) {
        return chunk.update;
    }

    throw new Error('Missing datasync chunk payload');
}

/**
 * Decode and handle DataSync messages.
 */
export function createDataSyncDecoder(
    handlers: DataSyncDecoderSynchronousHandlers
): DataSyncDecoderSynchronousFn;
export function createDataSyncDecoder(handlers: DataSyncDecoderHandlers): DataSyncDecoderFn {
    const pendingMessages = new Map<
        number,
        Array<protocol.IPromptInitMessage | protocol.IInitMessage | protocol.IUpdateMessage>
    >();

    const handleIncomingMessageChunk = <
        Message extends
            | protocol.IInitMessage
            | protocol.IUpdateMessage
            | protocol.IPromptInitMessage,
    >(
        chunk: Message,
        handle: (message: Message) => MaybePromise<void>
    ) => {
        if (
            chunk.id === undefined ||
            chunk.id === null ||
            chunk.index === undefined ||
            chunk.count === undefined
        ) {
            return handle(chunk);
        }

        const chunks = pendingMessages.get(chunk.id) || [];
        chunks.push(chunk);
        pendingMessages.set(chunk.id, chunks);

        // Have we received all the chunks?
        if (chunks.length === chunk.count) {
            let merged: Uint8Array;

            try {
                merged = mergeUint8Arrays(
                    chunks.sort((a, b) => (a.index || 0) - (b.index || 0)).map(getChunkPayload)
                );
            } finally {
                // Garbage-collect messages
                pendingMessages.delete(chunk.id);
            }

            const result = handle({
                ...chunk,
                ...('update' in chunk ? { update: merged } : { stateVector: merged }),
            });

            return result;
        }
    };

    return (rawMessage) => {
        return decodeDataSyncMessage(rawMessage, {
            promptInit: (message) => {
                return handleIncomingMessageChunk(message, (outputMessage) =>
                    handlers.promptInit(outputMessage)
                );
            },
            init: (message) => {
                return handleIncomingMessageChunk(message, (outputMessage) =>
                    handlers.init(outputMessage)
                );
            },
            update: (message) => {
                return handleIncomingMessageChunk(message, (outputMessage) =>
                    handlers.update(outputMessage)
                );
            },
            updatePresence: handlers.updatePresence,
            broadcastOperation: handlers.broadcastOperation,
            broadcastPresences: handlers.broadcastPresences,
            ack: handlers.ack,
            rpcReply: handlers.rpcReply,
            rpcRequest: handlers.rpcRequest,
            broadcastMarkers: handlers.broadcastMarkers,
        });
    };
}

export function createGeneralDataSyncDecoder(
    handler: (message: protocol.IMessage) => MaybePromise<void>
): DataSyncDecoderFn {
    const pendingMessages = new Map<
        number,
        Array<protocol.IPromptInitMessage | protocol.IInitMessage | protocol.IUpdateMessage>
    >();
    const handleIncomingMessageChunk = <
        Message extends
            | protocol.IInitMessage
            | protocol.IUpdateMessage
            | protocol.IPromptInitMessage,
    >(
        chunk: Message,
        handle: (message: Message) => MaybePromise<void>
    ) => {
        if (
            chunk.id === undefined ||
            chunk.id === null ||
            chunk.index === undefined ||
            chunk.count === undefined
        ) {
            return handle(chunk);
        }

        const chunks = pendingMessages.get(chunk.id) || [];
        chunks.push(chunk);
        pendingMessages.set(chunk.id, chunks);

        // Have we received all the chunks?
        if (chunks.length === chunk.count) {
            let merged: Uint8Array;

            try {
                merged = mergeUint8Arrays(
                    chunks.sort((a, b) => (a.index || 0) - (b.index || 0)).map(getChunkPayload)
                );
            } finally {
                // Garbage-collect messages
                pendingMessages.delete(chunk.id);
            }

            const result = handle({
                ...chunk,
                ...('update' in chunk ? { update: merged } : { stateVector: merged }),
            });

            return result;
        }
    };

    return (rawMessage) => {
        const message = protocol.Message.decode(rawMessage);
        const object = protocol.Message.toObject(message) as protocol.IMessage;
        if (object.promptInit) {
            return handleIncomingMessageChunk(object.promptInit, (outputMessage) =>
                handler({ promptInit: outputMessage })
            );
        }

        if (object.init) {
            return handleIncomingMessageChunk(object.init, (outputMessage) =>
                handler({ init: outputMessage })
            );
        }

        if (object.update) {
            return handleIncomingMessageChunk(object.update, (outputMessage) =>
                handler({ update: outputMessage })
            );
        }
        return handler(object);
    };
}

/**
 * Decode a message.
 */
function decodeDataSyncMessage(input: Uint8Array, handlers: DataSyncDecoderHandlers) {
    const message = protocol.Message.decode(input);
    const object = protocol.Message.toObject(message) as protocol.IMessage;

    if (object.promptInit) {
        return handlers.promptInit(object.promptInit);
    }

    if (object.init) {
        return handlers.init(object.init);
    }

    if (object.update) {
        return handlers.update(object.update);
    }

    if (object.updatePresence) {
        return handlers.updatePresence(object.updatePresence);
    }

    if (object.broadcastOperation) {
        return handlers.broadcastOperation(object.broadcastOperation);
    }

    if (object.broadcastPresences) {
        return handlers.broadcastPresences(object.broadcastPresences);
    }

    if (object.ack) {
        return handlers.ack(object.ack);
    }

    if (object.rpcRequest) {
        return handlers.rpcRequest(object.rpcRequest);
    }

    if (object.rpcReply) {
        return handlers.rpcReply(object.rpcReply);
    }

    if (object.broadcastMarkers) {
        return handlers.broadcastMarkers(object.broadcastMarkers);
    }
}
